import { Alert, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

type Props = {
  loading: boolean;
  /** the stations outside the radius could not be loaded */
  error: boolean;
  onRetry: () => void;
};

/**
 * Compact status shown at the top centre of the map. The wrapper lets pointer events through,
 * so panning, zooming and marker clicks keep working while stations load.
 */
const MapLoadingStatus = ({ loading, error, onRetry }: Props) => {
  const { t } = useTranslation();
  const { borderRadius, palette, zIndex } = useTheme();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        // keeps clear of the legend control in the top right corner
        maxWidth: 'calc(100% - 144px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        zIndex: zIndex.fab,
        pointerEvents: 'none',
      }}
    >
      {/* <output> is a polite status region, always mounted so screen readers announce when loading starts */}
      <Box component="output" aria-live="polite" display="block">
        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              paddingX: 1.5,
              paddingY: 0.75,
              backgroundColor: palette.common.white,
              borderRadius: borderRadius.sm,
              boxShadow: 1,
            }}
          >
            <CircularProgress size={16} aria-hidden />
            <Typography fontSize={13} fontWeight={600}>
              {t('siteLookup.map.loading')}
            </Typography>
          </Box>
        )}
      </Box>

      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('siteLookup.filters.retry')}
            </Button>
          }
          sx={{ paddingY: 0, boxShadow: 1, pointerEvents: 'auto' }}
        >
          {t('siteLookup.map.loadError')}
        </Alert>
      )}
    </Box>
  );
};

export default MapLoadingStatus;
